import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import screeningsData from '../data/screenings.json';
import filmsData from '../data/movies.json';
import {useNavigate} from 'react-router-dom';
import { slugify } from "./utils";
import '../styles/WeeklyCalendar.css';

const WeeklyCalendar = () => {
    const getInitialSelectedDate = () => {
        const storedDate = localStorage.getItem('selectedDate');
        return storedDate ? storedDate : new Date().toDateString();
    };

    const [currentWeek, setCurrentWeek] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(getInitialSelectedDate());
    const [screenings, setScreenings] = useState({});
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const screeningsPerPage = 4; // Nombre de séances par page

    useEffect(() => {
        localStorage.setItem('selectedDate', selectedDate);
    }, [selectedDate]);

    // Reset la pagination quand on change de date
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedDate]);

    const getCalendarDates = (date) => {
        const start = new Date(date);
        start.setDate(start.getDate() - start.getDay());
        return Array.from({ length: 14 }).map((_, i) => {
            const day = new Date(start);
            day.setDate(day.getDate() + i);
            return day;
        });
    };

    useEffect(() => {
        // Filtrer et trier les séances
        const groupedScreenings = screeningsData
            .filter(s => {
                const screeningDate = new Date(s.heure_de_sortie);
                return screeningDate > new Date() && s.filmId !== "#N/A";
            })
            .sort((a, b) => new Date(a.heure_de_sortie) - new Date(b.heure_de_sortie))
            .reduce((acc, screening) => {
                const date = new Date(screening.heure_de_sortie).toDateString();
                if (!acc[date]) acc[date] = [];
                // Vérifier si le film existe avant d'ajouter la séance
                const film = filmsData.find(f => f.id === screening.filmId);
                if (film) {
                    acc[date].push(screening);
                }
                return acc;
            }, {});

        setScreenings(groupedScreenings);
    }, []);

    const handleFilmClick = (filmId) => {
        navigate(`/film/${filmId}`);
    };

    // Pagination des séances
    const paginateScreenings = (screeningsList) => {
        if (!screeningsList) return [];
        const startIndex = (currentPage - 1) * screeningsPerPage;
        return screeningsList.slice(startIndex, startIndex + screeningsPerPage);
    };

    const totalPages = screenings[selectedDate] 
        ? Math.ceil(screenings[selectedDate].length / screeningsPerPage) 
        : 0;

    return (
        <div className="weekly-calendar">
            <div className="dates-scroller">
                {getCalendarDates(currentWeek).map((date, i) => {
                    const dateString = date.toDateString();
                    return (
                        <motion.div
                            key={i}
                            className={`date-card ${selectedDate === dateString ? 'selected' : ''}`}
                            onClick={() => setSelectedDate(dateString)}
                            whileHover={{ scale: 1.05 }}
                        >
                            <div className="day-name">
                                {date.toLocaleDateString('fr-FR', { weekday: 'short' })}
                            </div>
                            <div className="day-number">
                                {date.getDate()}
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            <div className="screenings-list">
                {selectedDate && screenings[selectedDate] ? (
                    <>
                        {paginateScreenings(screenings[selectedDate]).map((screening, i) => {
                            const film = filmsData.find(f => f.id === screening.filmId);
                            if (!film) return null;
                            return (
                                <motion.div
                                    key={i}
                                    className="screening-item"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    whileHover={{ scale: 1.02 }}
                                    onClick={() => handleFilmClick(slugify(film.titre))}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <img src={film.poster} alt={film.titre} className="film-poster" />
                                    <div className="screening-info">
                                        <h4>{film.titre}</h4>
                                        <p>
                                            {new Date(screening.heure_de_sortie).toLocaleTimeString('fr-FR', {
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            })}
                                        </p>
                                        <p>{screening.cinema}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                        
                        {totalPages > 1 && (
                            <div className="pagination-controls mt-4 flex justify-center gap-4 items-center">
                                <button 
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 bg-gray-100 rounded disabled:opacity-50"
                                >
                                    ◀
                                </button>
                                <span className="text-sm">
                                    Page {currentPage} sur {totalPages}
                                </span>
                                <button 
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 bg-gray-100 rounded disabled:opacity-50"
                                >
                                    ▶
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="no-screenings">
                        Aucune séance prévue cette journée
                    </div>
                )}
            </div>
        </div>
    );
};

export default WeeklyCalendar;